import {Iso8601} from '@hconnect/common/types'
import {
  DetailsCard,
  DropdownList,
  DropdownListItem,
  CardHeader,
  DropdownWithButton,
  Loading
} from '@hm/fundamental'
import {Grid, TableContainer} from '@mui/material'
import {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useGetCqdbMeasurements} from '../../hooks/api/labData'
import {CqdbEquipmentProductTypeDto} from '../../types'

import {LabQualityResultTable} from './LabQualityResultTable'

type Props = {
  plantId: string
  timeRange: {startDate?: Iso8601; endDate?: Iso8601}
  equipmentName: string
  productTypes?: CqdbEquipmentProductTypeDto[]
  latestEquipmentType?: string
}

export const LabQualityResultMeasurements: React.FC<Props> = ({
  plantId,
  equipmentName,
  productTypes,
  timeRange,
  latestEquipmentType
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined)

  const {data: measurements, isLoading} = useGetCqdbMeasurements(
    t('labQualityResult.unit'),
    plantId,
    equipmentName,
    timeRange,
    selectedItem
  )

  const dropdownItems = useMemo(
    () => productTypes?.map((item) => item.limsMaterialName) ?? [],
    [productTypes]
  )

  const isSelectedItemValid = selectedItem && dropdownItems.includes(selectedItem)

  useEffect(() => {
    if (dropdownItems.length === 0) return

    if (latestEquipmentType && dropdownItems.includes(latestEquipmentType)) {
      setSelectedItem(latestEquipmentType)
      return
    }

    // Initial load or invalid selection; default to first item
    if (!isSelectedItemValid) {
      setSelectedItem(dropdownItems[0])
    }
  }, [dropdownItems, isSelectedItemValid, latestEquipmentType])

  return (
    <TableContainer>
      <DetailsCard>
        <CardHeader>
          {!!productTypes?.length && (
            <DropdownWithButton variant="secondary" label={selectedItem}>
              <DropdownList onSelectItem={(item) => setSelectedItem(item.title?.toString())}>
                {dropdownItems.map((productType) => (
                  <DropdownListItem
                    title={productType}
                    key={productType}
                    data-cy={`cqdb-product-type-dropdown-item-${productType}`}
                  />
                ))}
              </DropdownList>
            </DropdownWithButton>
          )}
        </CardHeader>
        {isLoading && !selectedItem && (
          <Grid container justifyContent="center" alignItems="center" flex={1}>
            <Loading variant="secondary" size="large" />
          </Grid>
        )}
        {!!measurements?.rows.length && <LabQualityResultTable data={measurements} />}
        {!isLoading && !measurements?.rows.length && (
          <Grid container justifyContent="center" alignItems="center" padding={4}>
            {t('eventsTable.noData')}
          </Grid>
        )}
      </DetailsCard>
    </TableContainer>
  )
}
